import React, { FC, useState } from 'react';
import { View } from 'react-native';
import FastImage from 'react-native-fast-image';
import { Zoomable } from '@likashefqet/react-native-image-zoom';
import { fullHeight, fullWidth, scale as initialScale } from '@components/frame';
import ImageBase64 from './ImageBase64';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import { PictureProps } from '@v2/components/imageHandling/components/Picture';
import TouchableElement from '@components/common/touchableElement';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types.d';
import { useManageImage } from './hooks/useManageImage';
import { ATTACH_ITEMS_ON_TOP_OFF_IMAGE_LEFT, ATTACH_ITEMS_ON_TOP_OFF_IMAGE_TOP, IMAGE_ZOOM_CONSTRAINTS } from '@v2/components/imageHandling/constants';
import { useManageImageScalingTabletMobile } from './hooks/useManageImageScalingTabletMobile';
import Processing from '@v2/components/imageHandling/components/Processing';
import { isPhone } from '@helpers';
import AttachItemsGlass from '@components/common/AttachItemsGlass';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import { ContainerStyle } from '@interfaces/base';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface ProgressiveImageTabletMobileProps
  extends ContainerStyle,
    Pick<PictureProps, 'onImagePress' | 'withoutAttachItems' | 'withNotchTop' | 'zoomableOverlay'> {
  image: ImageDocumentStoreItem;
  width?: number;
  height?: number;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  resizeMode?: 'contain' | 'cover';
  withScalingPanning?: boolean;
  onZoomStart?: () => void;
}

const ProgressiveImageTabletMobile: FC<ProgressiveImageTabletMobileProps> = ({
  image,
  containerStyle,
  onImagePress,
  onZoomStart,
  withScalingPanning,
  withNotchTop,
  withoutAttachItems,
  zoomableOverlay,
  resizeMode = FastImage.resizeMode.contain,
  scale = initialScale,
  font = initialFont,
  width = fullWidth,
  height = fullHeight,
}) => {
  const [highResLoaded, setHighResLoaded] = useState(false);

  const insets = useSafeAreaInsets();

  const notchTop = withNotchTop ? insets.top : 0;

  const { angle, highResUri, lowResUri, imageHeight, imageWidth, shouldDisplayPreviewBase64, croppedImageURI, shouldDisplayCroppedImagePreview } =
    useManageImage({ image, width, height });

  const { zoomableRef, handleDoubleTap, handleProgrammaticZoom, handleResetAnimationEnd, handleSingleTap, handleZoomEnd, handleZoomStart } =
    useManageImageScalingTabletMobile({ onImagePress, onZoomStart, documentId: image?.documentId });

  if (shouldDisplayPreviewBase64) {
    return <ImageBase64 {...{ base64: image.base64, containerStyle, width, height, scale, font, angle }} />;
  }

  const imageContent = (
    <View style={{ width: imageWidth, height: imageHeight }}>
      {shouldDisplayCroppedImagePreview && (
        <View style={{ position: 'absolute', width: imageWidth, height: imageHeight }}>
          <FastImage source={{ uri: croppedImageURI }} style={{ width: '100%', height: '100%' }} resizeMode={resizeMode} />
        </View>
      )}

      {!shouldDisplayCroppedImagePreview && (
        <>
          {/* Low res image */}
          <View style={{ position: 'absolute', width: imageWidth, height: imageHeight }}>
            <FastImage source={{ uri: lowResUri }} style={{ width: '100%', height: '100%' }} resizeMode={resizeMode} />
          </View>

          {/* High res image */}
          <View style={[{ position: 'absolute', width: imageWidth, height: imageHeight, opacity: highResLoaded ? 1 : 0 }]}>
            <FastImage
              source={{ uri: highResUri }}
              style={{ width: '100%', height: '100%' }}
              onLoad={() => setHighResLoaded(true)}
              resizeMode={resizeMode}
            />
          </View>
        </>
      )}
    </View>
  );

  if (!withScalingPanning) {
    return (
      <View style={[{ width: imageWidth, height: imageHeight, transform: [{ rotate: `${angle}deg` }] }, containerStyle]}>
        <TouchableElement onPress={onImagePress}>{() => imageContent}</TouchableElement>
      </View>
    );
  }

  return (
    <View style={[{ position: 'relative', alignItems: 'center', justifyContent: 'center' }, containerStyle]}>
      <Zoomable
        ref={zoomableRef}
        minScale={IMAGE_ZOOM_CONSTRAINTS.min}
        maxScale={IMAGE_ZOOM_CONSTRAINTS.max}
        doubleTapScale={(IMAGE_ZOOM_CONSTRAINTS.max + IMAGE_ZOOM_CONSTRAINTS.min) / 2}
        isSingleTapEnabled
        isDoubleTapEnabled
        onInteractionStart={handleZoomStart}
        onInteractionEnd={handleZoomEnd}
        onSingleTap={handleSingleTap}
        onDoubleTap={handleDoubleTap}
        onProgrammaticZoom={handleProgrammaticZoom}
        onResetAnimationEnd={handleResetAnimationEnd}
        style={{ width: width, height: height, alignItems: 'center', justifyContent: 'center' }}
      >
        <View style={{ transform: [{ rotate: `${angle}deg` }], alignItems: 'center', justifyContent: 'center' }}>
          {imageContent}
          {/* Optional render slot — annotation overlay (when in drawing mode) is mounted here so it
              inherits Zoomable's transform and gestures compose in the same chain. */}
          {zoomableOverlay ? (
            // Pre-rotation (imageWidth/imageHeight) dims are intentional: the overlay lives inside the
            // rotated parent, so its local coord system matches the un-rotated image pixels that
            // annotations are stored in.
            <View
              testID={TEST_IDS.drawingCanvas.layer}
              pointerEvents="box-none"
              style={{ position: 'absolute', top: 0, left: 0, width: imageWidth, height: imageHeight }}
            >
              {zoomableOverlay}
            </View>
          ) : null}
        </View>
      </Zoomable>

      {!isPhone && !withoutAttachItems && (
        <View
          style={{
            position: 'absolute',
            top: scale(ATTACH_ITEMS_ON_TOP_OFF_IMAGE_TOP) + notchTop + scale(IMAGE_HANDLING_HEADER_HEIGHT),
            left: scale(ATTACH_ITEMS_ON_TOP_OFF_IMAGE_LEFT),
            zIndex: 1000000000,
            pointerEvents: 'box-none',
          }}
        >
          <AttachItemsGlass
            image={image}
            scale={scale}
            font={font}
            showMoreLinkConfig={{ shouldUseRelativeLeftPosition: true, topRelativeOffset: -(IMAGE_HANDLING_HEADER_HEIGHT / 2 - notchTop) }}
          />
        </View>
      )}
      {image.isProcessing && <Processing scale={scale} font={font} />}
    </View>
  );
};

export default ProgressiveImageTabletMobile;
